import { useState } from 'react';
import { useAppStore } from '../../store/useAppStore.js';
import { useToastStore } from '../../store/useToastStore.js';
import { createGoal, updateGoal, deleteGoal } from '../../api/client.js';
import { GoalMetric, GoalPeriod, GOAL_METRIC_LABELS } from '@launchpad/shared';

const PRESETS = [
  { title: 'Apply to 5 jobs this week', metric: GoalMetric.APPLICATIONS_SENT, target: 5, period: GoalPeriod.WEEKLY },
  { title: 'Send 3 outreach messages this week', metric: GoalMetric.NETWORKING_OUTREACH, target: 3, period: GoalPeriod.WEEKLY },
  { title: 'Complete 1 conversation this month', metric: GoalMetric.CONVERSATIONS_COMPLETED, target: 1, period: GoalPeriod.MONTHLY },
];

export function GoalsSection() {
  const goals = useAppStore((s) => s.goals);
  const addGoalLocal = useAppStore((s) => s.addGoalLocal);
  const updateGoalLocal = useAppStore((s) => s.updateGoalLocal);
  const removeGoalLocal = useAppStore((s) => s.removeGoalLocal);
  const addToast = useToastStore((s) => s.addToast);
  const [editing, setEditing] = useState(null); // null | { goalId?, title, metric, target, period }

  function startNew() {
    setEditing({ title: '', metric: GoalMetric.APPLICATIONS_SENT, target: 5, period: GoalPeriod.WEEKLY });
  }

  function startEdit(goal) {
    setEditing({ goalId: goal.goalId, title: goal.title, metric: goal.metric, target: goal.target, period: goal.period });
  }

  async function handleAdoptPreset(preset) {
    try {
      const created = await createGoal(preset);
      addGoalLocal(created);
      addToast('Goal added ✓', 'success');
    } catch (err) { addToast(err.message || 'Failed to create goal', 'error'); }
  }

  async function handleSave() {
    if (!editing.title.trim()) return;
    try {
      if (editing.goalId) {
        const updated = await updateGoal(editing.goalId, { title: editing.title.trim(), metric: editing.metric, target: Number(editing.target), period: editing.period });
        updateGoalLocal(editing.goalId, updated);
      } else {
        const created = await createGoal({ title: editing.title.trim(), metric: editing.metric, target: Number(editing.target), period: editing.period });
        addGoalLocal(created);
      }
      setEditing(null);
      addToast('Goal saved ✓', 'success');
    } catch (err) { addToast(err.message || 'Failed to save goal', 'error'); }
  }

  async function handleDelete(goalId) {
    try {
      await deleteGoal(goalId);
      removeGoalLocal(goalId);
      addToast('Goal deleted ✓', 'success');
    } catch { addToast('Failed to delete goal', 'error'); }
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-slate-700">Goals</h2>
        {!editing && <button onClick={startNew} className="text-xs text-primary-600 font-medium hover:underline">+ New Goal</button>}
      </div>

      {goals.length === 0 && !editing && (
        <div className="rounded border border-slate-200 p-4 space-y-3">
          <p className="text-xs text-slate-500">No goals yet. Try one of these:</p>
          {PRESETS.map((preset, i) => (
            <button key={i} onClick={() => handleAdoptPreset(preset)} className="block w-full text-left rounded border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
              <span className="font-medium text-slate-700">{preset.title}</span>
              <span className="text-xs text-slate-400 ml-2">{preset.period}</span>
            </button>
          ))}
        </div>
      )}

      {editing && (
        <div className="rounded border border-slate-200 p-3 space-y-2 mb-3">
          <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Goal title" maxLength={100} className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
          <div className="grid grid-cols-3 gap-2">
            <select value={editing.metric} onChange={(e) => setEditing({ ...editing, metric: e.target.value })} className="rounded border border-slate-300 px-2 py-1.5 text-xs">
              {Object.entries(GOAL_METRIC_LABELS).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
            <input type="number" min="1" max="999" value={editing.target} onChange={(e) => setEditing({ ...editing, target: e.target.value })} className="rounded border border-slate-300 px-2 py-1.5 text-xs" />
            <select value={editing.period} onChange={(e) => setEditing({ ...editing, period: e.target.value })} className="rounded border border-slate-300 px-2 py-1.5 text-xs">
              {Object.values(GoalPeriod).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} className="rounded bg-primary-600 px-3 py-1 text-xs font-medium text-white hover:bg-primary-700">Save</button>
            <button onClick={() => setEditing(null)} className="rounded px-3 py-1 text-xs text-slate-600 hover:bg-slate-100">Cancel</button>
          </div>
        </div>
      )}

      {goals.length > 0 && (
        <div className="space-y-1">
          {goals.map((goal) => (
            <div key={goal.goalId} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm text-slate-700 truncate">{goal.title}</p>
                <p className="text-[10px] text-slate-400">{GOAL_METRIC_LABELS[goal.metric]} · {goal.target} / {goal.period}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => startEdit(goal)} className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">Edit</button>
                <button onClick={() => handleDelete(goal.goalId)} className="rounded px-2 py-1 text-xs text-destructive-600 hover:bg-destructive-500/10">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
